import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { LogoLoading } from '@/components/common';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInactiveAlert, setShowInactiveAlert] = useState(false);
  const { signIn, user } = useAuthContext();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redirecionar se o usuário já estiver autenticado
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Verificar a conexão com o Supabase ao carregar a página
  useEffect(() => {
    async function checkSupabaseConnection() {
      try {
        console.log('Verificando conexão com o Supabase...');
        console.log('URL do Supabase:', import.meta.env.VITE_SUPABASE_URL);
        
        // Verificar se podemos obter a sessão atual
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Erro ao verificar a sessão:', error);
        } else {
          console.log('Sessão atual:', data);
        }
      } catch (error) {
        console.error('Erro ao verificar a conexão com o Supabase:', error);
      }
    }
    
    checkSupabaseConnection();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple simultaneous submissions
    if (loading) {
      console.log('🚫 Login already in progress, ignoring submission');
      return;
    }
    
    setLoading(true);
    setShowInactiveAlert(false);

    try {
      console.log('🔍 Attempting login with:', email);
      const result = await signIn(email, password);
      
      if (!result?.user) {
        throw new Error('Login failed: No user data received');
      }
      
      console.log('✅ Login successful:', result);
      // Redirect will happen automatically via useEffect when user state updates
      
    } catch (error: any) {
      console.error('❌ Login error:', error);
      
      // Check if it's an inactive user error
      const isInactiveUserError = error?.message?.toLowerCase().includes('inactive') || 
                                 error?.message?.toLowerCase().includes('disabled') ||
                                 error?.message?.toLowerCase().includes('blocked');
      
      // Full error log for debugging
      console.log('🔍 Complete login error:', {
        message: error?.message,
        code: error?.code,
        status: error?.status,
        error: error
      });

      if (isInactiveUserError) {
        console.log('🚫 Detected: Inactive user');
        setShowInactiveAlert(true);
        toast({
          variant: "destructive",
          title: "Access Blocked",
          description: error.message || "Your account has been deactivated. Please contact the administrator to reactivate your access.",
        });
      } else {
        // For any other error, show invalid credentials message
        console.log('🔴 Showing login error toast');
        toast({
          variant: "destructive",
          title: "Invalid Credentials",
          description: "Email or password incorrect. Please verify your credentials and try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset alert when user starts typing
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (showInactiveAlert) setShowInactiveAlert(false);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (showInactiveAlert) setShowInactiveAlert(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* Logo da Trivium */}
          <div className="flex justify-center mb-6">
            <img 
              src="https://cdn.brandfetch.io/idNdb_MPJ2/w/4500/h/1145/theme/dark/logo.png?c=1dxbfHSJFAPEGdCLU4o5B" 
              alt="Trivium Logo" 
              className="h-12 object-contain"
            />
          </div>
          <CardTitle>Customer Service Management</CardTitle>
          <CardDescription>
            Entre com suas credenciais para acessar sua conta
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-extrabold">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={handleEmailChange}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-extrabold">
                Senha
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <LogoLoading size={20} />
                  <span>Entrando...</span>
                </div>
              ) : (
                'Entrar'
              )}
            </Button>
            
            <div className="text-sm text-center space-y-2">
              <p>
                <Link to="/reset-password" className="text-primary hover:underline">
                  Esqueceu sua senha?
                </Link>
              </p>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
} 